import { JobRoleDto, JobRolesListElement } from 'types/jobRoles';
import { PersonalRateDraft, PricelistReference, PricelistTabDraft } from 'types/matters';
import {
    HourlyRate,
    MatterFollowedPricelist,
    MatterPricelist,
    MatterPricelistWriteDto,
    PersonalRateInput,
    PricelistPeriod,
    PricingKind,
    RateFigures,
} from 'types/pricelists';
import { t } from 'utils/getTranslations';
import { sortPeriods } from './employeePricing';

export type DateError = 'required' | 'not-after-opening' | 'duplicate';

export interface TabValidation {
    missingRolePrice: boolean;
    unpricedEmployeeIds: number[];
    dateError: DateError | null;
}

export type SaveOperation =
    | { type: 'delete'; pricelistId: number; label: string }
    | {
          type: 'create';
          key: string;
          validFrom: string;
          name: string | null;
          rates: HourlyRate[];
          personalRates: PersonalRateInput[];
          label: string;
      }
    | {
          type: 'rows';
          pricelistId: number | null;
          rates: HourlyRate[] | null;
          personalRates: PersonalRateInput[] | null;
          label: string;
      }
    | { type: 'header'; pricelistId: number; validFrom: string | null; name: string | null; label: string };

const normalizePrice = (price: string | number | null | undefined): string | null =>
    price === null || price === undefined || price === '' ? null : String(Number(price));

export const toPersonalRateInputs = (rows: PersonalRateDraft[]): PersonalRateInput[] =>
    rows.map((row) => ({ employee: { id: row.employee.id }, hourlyPrice: normalizePrice(row.hourlyPrice) }));

// The grid shows every active role plus the inactive roles the list already prices, as the shared modal does.
export const buildRoleRows = (jobRoles: JobRolesListElement[], existing: HourlyRate[]): HourlyRate[] =>
    jobRoles
        .filter((jobRole) => {
            const stored = existing.find((rate) => rate.jobRole.id === jobRole.id);
            const hasValue =
                stored?.hourlyPrice !== null && stored?.hourlyPrice !== undefined && stored?.hourlyPrice !== '';
            return jobRole.isActive || hasValue;
        })
        .map((jobRole) => ({
            jobRole,
            hourlyPrice: existing.find((rate) => rate.jobRole.id === jobRole.id)?.hourlyPrice?.toString() ?? '',
        }));

// A stored list as a tab, its role rows as read; the editor runs them through buildRoleRows when it opens.
export const tabFromList = (list: MatterPricelist): PricelistTabDraft => ({
    key: `list-${list.id}`,
    pricingKind: list.pricingKind,
    id: list.id,
    isBase: list.validFrom === null,
    validFrom: list.validFrom,
    name: list.name,
    rates: list.rates,
    personalRates: list.personalRates.map((row) => ({
        employee: row.employee,
        jobRole: row.jobRole,
        hourlyPrice: row.hourlyPrice,
    })),
});

// The base tab of a case that has no list yet, or one whose lists a currency change wiped.
export const baseTabWithRates = (rates: HourlyRate[] = []): PricelistTabDraft => ({
    key: 'base',
    id: null,
    isBase: true,
    validFrom: null,
    name: null,
    rates,
    personalRates: [],
});

// The undated list, applying from the case's opening date.
export const baseListOf = <T extends { validFrom: string | null }>(lists: T[]): T | undefined =>
    lists.find((list) => list.validFrom === null);

// Base first, then by date: the order the server returns stored lists in, restored after the editor's edits.
export const sortTabs = (tabs: PricelistTabDraft[]): PricelistTabDraft[] =>
    tabs.toSorted((a, b) => {
        if (a.isBase !== b.isBase) return a.isBase ? -1 : 1;
        return (a.validFrom ?? '').localeCompare(b.validFrom ?? '');
    });

// Keys of added tabs count on from the highest one present, so a tab staged by an earlier Apply keeps its key.
export const nextNewTabKey = (tabs: { key: string }[]): string => {
    const taken = tabs.map((tab) => Number(/^new-(\d+)$/.exec(tab.key)?.[1] ?? 0));
    return `new-${Math.max(0, ...taken) + 1}`;
};

// Lists come from the server in date order, the base list first; the list in force on a date is the last
// one applying on or before it, and the base list covers everything before the first dated list.
export const inForceListOn = <T extends { validFrom: string | null }>(lists: T[], date: string): T | undefined =>
    lists.reduce<T | undefined>(
        (inForce, list) => (list.validFrom === null || list.validFrom <= date ? list : inForce),
        undefined
    );

export const tabLabel = (
    tab: { isBase: boolean; validFrom: string | null; name: string | null },
    openedDate: string | null,
    formatDate: (date: string) => string,
    fromDate: (date: string) => string,
    // A tab the user just added has neither date nor name yet; only the editor can produce one.
    newListLabel?: string
): string => {
    if (tab.isBase) {
        return openedDate ? formatDate(openedDate) : '';
    }
    if (tab.name) {
        return tab.name;
    }
    if (tab.validFrom) {
        return fromDate(formatDate(tab.validFrom));
    }
    return newListLabel ?? fromDate('…');
};

// A per-employee list's first period has no start date: it covers all work before the period that follows it, so a
// case following the list shows it as such, not from the case's opening date.
export const undatedPeriodLabel = (
    periods: { validFrom: string | null }[],
    formatDate: (date: string) => string
): string => {
    const nextStart = periods
        .map((period) => period.validFrom)
        .filter((validFrom): validFrom is string => validFrom !== null)
        .toSorted()[0];
    return nextStart
        ? t('pricelists.employee.period-before', { date: formatDate(nextStart) })
        : t('pricelists.employee.period-all-dates');
};

// An employee-priced case prices people only, and its dated lists may start on or before its opening date.
export const validateTab = (
    tab: PricelistTabDraft,
    tabs: PricelistTabDraft[],
    openedDate: string | null,
    pricingKind: PricingKind = PricingKind.ROLE
): TabValidation => {
    const perEmployee = pricingKind === PricingKind.EMPLOYEE;
    const missingRolePrice =
        !perEmployee && tab.rates.some((rate) => rate.jobRole.isActive && normalizePrice(rate.hourlyPrice) === null);
    const unpricedEmployeeIds = tab.personalRates
        .filter((row) => normalizePrice(row.hourlyPrice) === null)
        .map((row) => row.employee.id);
    let dateError: DateError | null = null;
    if (!tab.isBase) {
        if (!tab.validFrom) {
            dateError = 'required';
        } else if (!perEmployee && openedDate && tab.validFrom <= openedDate) {
            dateError = 'not-after-opening';
        } else if (tabs.some((other) => other.key !== tab.key && other.validFrom === tab.validFrom)) {
            dateError = 'duplicate';
        }
    }
    return { missingRolePrice, unpricedEmployeeIds, dateError };
};

export const hasValidationErrors = (validation: TabValidation): boolean =>
    validation.missingRolePrice || validation.unpricedEmployeeIds.length > 0 || validation.dateError !== null;

const sameRoleRows = (a: HourlyRate[], b: HourlyRate[]): boolean => {
    const priced = (rows: HourlyRate[]) =>
        rows
            .filter((row) => normalizePrice(row.hourlyPrice) !== null)
            .map((row) => `${row.jobRole.id}:${normalizePrice(row.hourlyPrice)}`)
            .toSorted();
    const left = priced(a);
    const right = priced(b);
    return left.length === right.length && left.every((entry, index) => entry === right[index]);
};

const samePersonalRows = (a: PersonalRateDraft[], b: PersonalRateDraft[]): boolean => {
    const keyed = (rows: PersonalRateDraft[]) =>
        rows.map((row) => `${row.employee.id}:${normalizePrice(row.hourlyPrice)}`).toSorted();
    const left = keyed(a);
    const right = keyed(b);
    return left.length === right.length && left.every((entry, index) => entry === right[index]);
};

// A role row without a price is not sent: the shared modal drops those too, so an inactive role the user
// left empty does not become a stored row.
export const pricedRoleRows = (rows: HourlyRate[]): HourlyRate[] =>
    rows.filter((row) => normalizePrice(row.hourlyPrice) !== null);

// A stored row on a since-deleted role is read back but refused when written; the first save drops it.
const rowsOnExistingRoles = (rows: HourlyRate[]): HourlyRate[] => rows.filter((row) => !row.jobRole.deletedAt);

const hasPricing = (tab: PricelistTabDraft): boolean =>
    pricedRoleRows(tab.rates).length > 0 || tab.personalRates.length > 0;

/**
 * The full set a case save carries: every staged tab as its list's complete state, the stored id included.
 * Nothing is null to mean "unchanged". A case without lists whose base tab is still empty sends an empty
 * set, so no empty base list is created — unless the save must carry the tab, as a currency change must.
 */
export const toPricelistWriteDtos = (tabs: PricelistTabDraft[], keepEmptyBase = false): MatterPricelistWriteDto[] => {
    const [only] = tabs;
    if (!keepEmptyBase && tabs.length === 1 && only.isBase && only.id === null && !hasPricing(only)) {
        return [];
    }
    return tabs.map((tab) => ({
        id: tab.id,
        validFrom: tab.validFrom,
        name: tab.name,
        rates: pricedRoleRows(rowsOnExistingRoles(tab.rates)),
        personalRates: toPersonalRateInputs(tab.personalRates),
    }));
};

// Whether the staged tabs hold anything a currency change would take: a priced base tab or any other tab.
export const hasStagedPricing = (tabs: PricelistTabDraft[]): boolean => tabs.length > 1 || tabs.some(hasPricing);

/** A kind switch leaves one empty list, which saves as it is: the case's work bills nothing until it is priced. */
export const isEmptyListOfKindSwitch = (
    tabs: PricelistTabDraft[],
    pricingKind: PricingKind,
    storedPricingKind: PricingKind
): boolean => pricingKind !== storedPricingKind && !hasStagedPricing(tabs);

/**
 * The stored-figures formula, for a staged list that has no stored figures yet: priced role rows on existing
 * roles not excluded from the range, plus every personal row; 0 when empty.
 */
export const rateFigures = (rates: HourlyRate[], personalRates: PersonalRateDraft[]): RateFigures => {
    const prices = [
        ...pricedRoleRows(rowsOnExistingRoles(rates))
            .filter((row) => !row.jobRole.excludeFromMinmaxCalculation)
            .map((row) => Number(row.hourlyPrice)),
        ...personalRates
            .filter((row) => normalizePrice(row.hourlyPrice) !== null)
            .map((row) => Number(row.hourlyPrice)),
    ];
    if (prices.length === 0) {
        return { minHourlyPrice: 0, maxHourlyPrice: 0, avgHourlyPrice: 0 };
    }
    return {
        minHourlyPrice: Math.min(...prices),
        maxHourlyPrice: Math.max(...prices),
        avgHourlyPrice: prices.reduce((sum, price) => sum + price, 0) / prices.length,
    };
};

// The earliest staged dated tab the opening date lands on or past; the date must stay before every list but an
// employee-priced case's, whose dated lists may start on or before it.
export const datedTabBlockingOpeningDate = (
    tabs: PricelistTabDraft[],
    openedDate: string | null
): PricelistTabDraft | undefined =>
    openedDate
        ? sortTabs(tabs).find(
              (tab) =>
                  !tab.isBase &&
                  tab.pricingKind !== PricingKind.EMPLOYEE &&
                  tab.validFrom !== null &&
                  tab.validFrom <= openedDate
          )
        : undefined;

export type PersonalRateStatus = 'NO_LONGER_ACTIVE' | 'NOT_ON_CASE_TEAM';

/**
 * What an individual-rate row tells about its person, as information only: anyone active may be priced, and
 * a person who left keeps their price. Nobody reads as inactive until the active employees have loaded.
 */
export function personalRateStatus(
    employeeId: number,
    caseTeamIds: Set<number>,
    activeEmployeeIds: Set<number> | null
): PersonalRateStatus | null {
    if (activeEmployeeIds && !activeEmployeeIds.has(employeeId)) {
        return 'NO_LONGER_ACTIVE';
    }
    return caseTeamIds.has(employeeId) ? null : 'NOT_ON_CASE_TEAM';
}

export const pricedEmployeeIds = (tabs: PricelistTabDraft[]): Set<number> =>
    new Set(tabs.flatMap((tab) => tab.personalRates.map((row) => row.employee.id)));

interface HeaderMove {
    tab: PricelistTabDraft;
    stored: PricelistTabDraft;
}

/**
 * Re-dates in an order where each one lands on a date no surviving list holds at that moment, so a list
 * moving away frees its date before whatever takes it. Two lists swapping dates have no such order; they
 * keep their submitted order and the second one fails on the unique, as it does today. `occupied` is
 * consumed: it starts as the surviving lists' stored dates and ends as their new ones.
 */
const orderHeaderMoves = (moves: HeaderMove[], occupied: Map<string, number>): HeaderMove[] => {
    const remaining = [...moves];
    const ordered: HeaderMove[] = [];
    while (remaining.length > 0) {
        const index = remaining.findIndex(({ tab }) => {
            const holder = tab.validFrom === null ? undefined : occupied.get(tab.validFrom);
            return holder === undefined || holder === tab.id;
        });
        if (index === -1) {
            return [...ordered, ...remaining];
        }
        const [move] = remaining.splice(index, 1);
        if (move.stored.validFrom !== null) {
            occupied.delete(move.stored.validFrom);
        }
        if (move.tab.validFrom !== null) {
            occupied.set(move.tab.validFrom, move.tab.id as number);
        }
        ordered.push(move);
    }
    return ordered;
};

/**
 * The calls the editor's Save issues, in the order that never collides: deletes first (a re-dated tab may
 * take a dropped list's date), then the base list's rows, then the kept dated tabs' dates and names, then
 * the new tabs, then every dated tab's rows. A tab equal to its stored list yields nothing.
 */
export const computeSaveOperations = (
    initial: PricelistTabDraft[],
    tabs: PricelistTabDraft[],
    label: (tab: PricelistTabDraft) => string
): SaveOperation[] => {
    const operations: SaveOperation[] = [];
    const keptIds = new Set(tabs.map((tab) => tab.id).filter((id): id is number => id !== null));
    initial
        .filter((tab) => !tab.isBase && tab.id !== null && !keptIds.has(tab.id))
        .forEach((tab) => operations.push({ type: 'delete', pricelistId: tab.id as number, label: label(tab) }));

    const rowsOperation = (tab: PricelistTabDraft, stored: PricelistTabDraft | undefined): SaveOperation | null => {
        const ratesChanged = !stored || !sameRoleRows(stored.rates, tab.rates);
        const personalChanged = !stored || !samePersonalRows(stored.personalRates, tab.personalRates);
        if (!ratesChanged && !personalChanged) {
            return null;
        }
        return {
            type: 'rows',
            pricelistId: tab.id,
            rates: ratesChanged ? pricedRoleRows(tab.rates) : null,
            personalRates: personalChanged ? toPersonalRateInputs(tab.personalRates) : null,
            label: label(tab),
        };
    };

    const base = tabs.find((tab) => tab.isBase);
    const storedBase = initial.find((tab) => tab.isBase);
    if (base) {
        const rows = rowsOperation(base, storedBase);
        if (rows) {
            operations.push(rows);
        }
    }

    const keptDated = tabs.filter((tab) => !tab.isBase && tab.id !== null);
    const storedOf = (tab: PricelistTabDraft) => initial.find((candidate) => candidate.id === tab.id);

    const moves = keptDated
        .map((tab) => ({ tab, stored: storedOf(tab) }))
        .filter((move): move is HeaderMove => {
            const { tab, stored } = move;
            return !!stored && (stored.validFrom !== tab.validFrom || (stored.name ?? null) !== (tab.name ?? null));
        });
    const occupied = new Map<string, number>();
    initial
        .filter((tab) => !tab.isBase && tab.id !== null && keptIds.has(tab.id) && tab.validFrom !== null)
        .forEach((tab) => occupied.set(tab.validFrom as string, tab.id as number));
    orderHeaderMoves(moves, occupied).forEach(({ tab }) =>
        operations.push({
            type: 'header',
            pricelistId: tab.id as number,
            validFrom: tab.validFrom,
            name: tab.name,
            label: label(tab),
        })
    );

    tabs.filter((tab) => !tab.isBase && tab.id === null).forEach((tab) =>
        operations.push({
            type: 'create',
            key: tab.key,
            validFrom: tab.validFrom ?? '',
            name: tab.name,
            rates: pricedRoleRows(tab.rates),
            personalRates: toPersonalRateInputs(tab.personalRates),
            label: label(tab),
        })
    );

    keptDated.forEach((tab) => {
        const rows = rowsOperation(tab, storedOf(tab));
        if (rows) {
            operations.push(rows);
        }
    });

    if (base && storedBase && (storedBase.name ?? null) !== (base.name ?? null) && base.id !== null) {
        operations.push({ type: 'header', pricelistId: base.id, validFrom: null, name: base.name, label: label(base) });
    }
    return operations;
};

// What the editor stages besides the tabs' rows: the case's kind, the list it follows and the referenced template.
export interface StagedPricing {
    pricingKind: PricingKind;
    followsPricelist: MatterFollowedPricelist | null;
    referencedPricelist: PricelistReference;
    tabs: PricelistTabDraft[];
}

/**
 * A kind change wipes the case's lists to one empty base tab and ends any following; the state before the first
 * change is kept aside, so switching back before the save restores it as it was.
 */
export function switchStagedKind(
    current: StagedPricing,
    pricingKind: PricingKind,
    beforeSwitch: StagedPricing | null
): { staged: StagedPricing; beforeSwitch: StagedPricing | null } {
    if (pricingKind === current.pricingKind) {
        return { staged: current, beforeSwitch };
    }
    if (beforeSwitch?.pricingKind === pricingKind) {
        return { staged: beforeSwitch, beforeSwitch: null };
    }
    return {
        staged: { pricingKind, followsPricelist: null, referencedPricelist: { id: null }, tabs: [baseTabWithRates()] },
        beforeSwitch: beforeSwitch ?? current,
    };
}

/**
 * The tabs an own copy keeps, as the server makes it: the followed periods that started by today, with the ids of
 * the case's lists that hold them, so they can be edited before the save.
 */
export const ownCopyTabs = (tabs: PricelistTabDraft[], today: string): PricelistTabDraft[] =>
    tabs.filter((tab) => tab.validFrom === null || tab.validFrom <= today);

// A followed list's periods as the tabs the case will hold once the save makes it follow, for the card to show.
export function tabsFromPeriods(periods: PricelistPeriod[] | null | undefined): PricelistTabDraft[] {
    const tabs = sortPeriods(periods ?? []).map((period, index) => ({
        key: `followed-${period.id ?? index}`,
        pricingKind: PricingKind.EMPLOYEE,
        id: null,
        isBase: period.validFrom === null,
        validFrom: period.validFrom,
        name: null,
        rates: [],
        personalRates: period.rates
            .filter((rate) => normalizePrice(rate.hourlyPrice) !== null)
            .map((rate) => ({
                employee: rate.employee,
                jobRole:
                    rate.jobRoleId === null
                        ? null
                        : ({
                              id: rate.jobRoleId,
                              name: rate.jobRoleName ?? '',
                              internalName: rate.jobRoleName ?? '',
                          } as JobRoleDto),
                hourlyPrice: rate.hourlyPrice,
            })),
    }));
    return tabs.some((tab) => tab.isBase) ? tabs : [baseTabWithRates(), ...tabs];
}

// The case form's required price list: a link, or a priced base list.
export const isPricingStaged = (
    tabs: PricelistTabDraft[],
    followsPricelist: MatterFollowedPricelist | null
): boolean => {
    if (followsPricelist) {
        return true;
    }
    const base = tabs.find((tab) => tab.isBase);
    return !!base && (pricedRoleRows(base.rates).length > 0 || base.personalRates.length > 0);
};

/**
 * The staged tabs against the lists the server holds after a kind switch or an own copy: the base tab takes the
 * stored base list's id, and a dated tab whose list is gone becomes a list to create.
 */
export function adoptStoredIds(tabs: PricelistTabDraft[], stored: PricelistTabDraft[]): PricelistTabDraft[] {
    const storedIds = new Set(stored.map((tab) => tab.id));
    const storedBaseId = stored.find((tab) => tab.isBase)?.id ?? null;
    return tabs.map((tab) => {
        if (tab.isBase) {
            return { ...tab, id: storedBaseId };
        }
        return tab.id !== null && storedIds.has(tab.id) ? tab : { ...tab, id: null };
    });
}
